import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import debounce from "lodash.debounce";
import { toast } from "react-toastify";

import { CompanyId } from "../constants";
import LocalstorageService from "../helpers/localstorage-service";
import { setLoadingOverlay } from "../store/slices/loadingOverlaySlice";

/**
 * Custom hook that consolidates the common list page logic:
 * fetching, pagination, sorting, search, delete modal, and loading overlay.
 *
 * @param {Object} options
 * @param {Object}  options.service        - Service object (e.g. AgentService)
 * @param {string}  options.listMethod     - Method name on service for listing (e.g. 'listAgent')
 * @param {string}  [options.deleteMethod] - Method name on service for deleting (e.g. 'deleteAgent')
 * @param {boolean} [options.companyId]    - If true, includes companyId from localStorage in payload (default true)
 * @param {Object}  [options.initialSort]  - Initial sort config, e.g. { value: 'fullName', asc: true }
 * @param {number}  [options.initialPageSize] - Items per page on mount (default 10)
 * @param {string}  [options.deleteIdField]   - Field name used as the id in the delete payload (default 'id')
 * @param {Function} [options.buildPayload]   - Optional function(basePayload) to add extra fields to the request payload
 */
export default function useListPage({
  service,
  listMethod,
  deleteMethod,
  companyId: includeCompanyId = true,
  initialSort = null,
  initialPageSize = 10,
  deleteIdField = "id",
  buildPayload,
}) {
  const dispatch = useDispatch();

  const [list, setList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(initialPageSize);
  const [search, setSearch] = useState("");
  const [currentSort, setCurrentSort] = useState(
    initialSort ? [initialSort] : []
  );

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState({ show: false, item: null });

  // Store companyId in a ref so it's captured once on mount
  const companyIdRef = useRef(null);
  // Track whether the initial mount fetch has happened
  const mountedRef = useRef(false);

  // ------------------------------------------------------------------
  // Fetch list
  // ------------------------------------------------------------------
  const fetchList = useCallback(
    async (pageNo, pageSize, searchTerm, sort) => {
      const payload = {
        pageNo,
        pageSize,
      };

      if (includeCompanyId && companyIdRef.current) {
        payload.companyId = companyIdRef.current;
      }

      if (sort && sort.length > 0) {
        payload.sort = sort;
      }

      if (searchTerm) {
        payload.search = searchTerm;
      }

      // Allow consumer to inject extra payload fields
      if (typeof buildPayload === "function") {
        buildPayload(payload);
      }

      setIsLoading(true);
      dispatch(setLoadingOverlay(true));

      try {
        const response = await service[listMethod](payload);
        const { status, message, dataList, totalCount: tc } = response.data;

        if (status === 400) {
          toast.error(message);
          return;
        }

        setList(dataList || []);
        setTotalCount(tc || 0);
      } catch (error) {
        console.error(`Error fetching ${listMethod}:`, error);
        toast.error("\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D8\u05E2\u05D9\u05E0\u05EA \u05E0\u05EA\u05D5\u05E0\u05D9\u05DD");
      } finally {
        setIsLoading(false);
        dispatch(setLoadingOverlay(false));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [service, listMethod, includeCompanyId, buildPayload, dispatch]
  );

  // ------------------------------------------------------------------
  // Mount: read companyId and do initial fetch
  // ------------------------------------------------------------------
  useEffect(() => {
    companyIdRef.current = LocalstorageService.getItem(CompanyId);
    const sort = initialSort ? [initialSort] : [];
    fetchList(1, initialPageSize, "", sort);
    mountedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------------------------
  // Search changed -> reset to page 1 and re-fetch (skip first mount)
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!mountedRef.current) return;

    const timeoutId = setTimeout(() => {
      setCurrentPage(1);
      fetchList(1, itemsPerPage, search, currentSort);
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // ------------------------------------------------------------------
  // Handlers
  // ------------------------------------------------------------------

  const handlePageClick = useCallback(
    (pageNo) => {
      setCurrentPage(pageNo);
      fetchList(pageNo, itemsPerPage, search, currentSort);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemsPerPage, search, currentSort, fetchList]
  );

  const handlePageSizeChange = useCallback(
    (e) => {
      const newSize = parseInt(e.target.value, 10);
      setItemsPerPage(newSize);
      setCurrentPage(1);
      fetchList(1, newSize, search, currentSort);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search, currentSort, fetchList]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((e) => {
      setSearch(e.target.value?.trim() || "");
    }, 500),
    []
  );

  const sortBy = useCallback(
    (sortObj) => {
      const newSort = sortObj
        ? [sortObj]
        : initialSort
          ? [initialSort]
          : [];
      setCurrentSort(newSort);
      fetchList(currentPage, itemsPerPage, search, newSort);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentPage, itemsPerPage, search, initialSort, fetchList]
  );

  // ------------------------------------------------------------------
  // Delete modal helpers
  // ------------------------------------------------------------------

  const openDeleteModal = useCallback((item) => {
    setDeleteModal({ show: true, item });
  }, []);

  const closeDeleteModal = useCallback(() => {
    setDeleteModal({ show: false, item: null });
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteMethod || !deleteModal.item) return;

    setIsLoading(true);
    try {
      const response = await service[deleteMethod]({
        [deleteIdField]: deleteModal.item[deleteIdField],
      });
      const { status, message } = response.data;

      if (status === 400) {
        toast.error(message);
        return;
      }

      toast.success(message);
      fetchList(currentPage, itemsPerPage, search, currentSort);
    } finally {
      setIsLoading(false);
    }

    closeDeleteModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    service,
    deleteMethod,
    deleteIdField,
    deleteModal.item,
    currentPage,
    itemsPerPage,
    search,
    currentSort,
    fetchList,
    closeDeleteModal,
  ]);

  // ------------------------------------------------------------------
  // Public API
  // ------------------------------------------------------------------
  return {
    list,
    isLoading,
    totalCount,
    currentPage,
    itemsPerPage,
    currentSort,
    search,
    deleteModal,
    fetchList: () => fetchList(currentPage, itemsPerPage, search, currentSort),
    handlePageClick,
    handlePageSizeChange,
    debouncedSearch,
    sortBy,
    openDeleteModal,
    closeDeleteModal,
    confirmDelete,
  };
}
